import config from 'constants/config'
import {
  KLARNA_PAYMENT_TYPE,
  KLARNA_BP_PAYMENT_TYPE,
  MIN_KLARNA_PAYMENT,
  MAX_KLARNA_PAYMENT,
  MIN_KLARNA_BP_GBP_PAYMENT,
  MIN_KLARNA_BP_NZD_PAYMENT,
  MIN_KLARNA_BP_USD_PAYMENT,
  MAX_KLARNA_BP_GBP_PAYMENT,
  MAX_KLARNA_BP_NZD_PAYMENT,
  MAX_KLARNA_BP_USD_PAYMENT,
  BRAINTREE_PAYMENT_TYPE,
  paymentMethodLabels,
  APPLE_PAY_PAYMENT_TYPE,
  BASE_MERCHANT_FEE,
  GOOGLE_PAY_PAYMENT_TYPE,
  GO_CARDLESS_PAYMENT_TYPE,
  STRIPE_CUSTOM_PAYTO_TYPE,
  STRIPE_PAYMENT_ELEMENT_CARD_TYPE,
  STRIPE_PAYMENT_ELEMENT_KLARNA_TYPE,
  SECURE_PAYMENT_TYPE,
  SHORT_LEAD_TIME_WITH_CREDIT_MODAL_LEAD_TIME_DAYS,
  SHORT_LEAD_TIME_WITH_CREDIT_MODAL_PERCENTAGE_THRESHOLD,
  PAYPAL_PAYMENT_TYPE, PAYLATER_PAYMENT_TYPE,
  FLYING_BLUE_PAYMENT_TYPE,
  STRIPE_PAYMENT_TYPE,
  RAZORPAY_PAYMENT_TYPE,
  CREDIT_PAYMENT_TYPE,
  DEBIT_CARD_MERCHANT_FEE,
  EXPRESS_PAYMENT_TYPE,
  PaymentMethod,
  STRIPE_PAYMENT_ELEMENT_AFTERPAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_GIROPAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_IDEAL_TYPE,
  STRIPE_PAYMENT_ELEMENT_PAYNOW_TYPE,
  STRIPE_PAYMENT_ELEMENT_GRABPAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_WECHATPAY_TYPE,
  VELOCITY_PAYMENT_TYPE,
  AZUPAY_PAY_ID_PAYMENT_TYPE,
  STRIPE_TERMINAL_PAYMENT_TYPE,
} from 'constants/payment'
import getPaymentMethods from 'lib/payment/getPaymentMethods'
import { isAfterPayBpEnabled, isWithinAfterPayBpAvailabilityRange } from 'lib/payment/afterPayBpUtils'
import { isHoolahBpEnabled, isWithinHoolahBpAvailabilityRange } from 'lib/payment/hoolahBpUtils'
import { isAtomeBpEnabled, isWithinAtomeBpAvailabilityRange } from 'lib/payment/atomeBpUtils'
import { isWaaveBpEnabled, isWithinWaaveBpAvailabilityRange } from 'lib/payment/waaveBpUtils'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { PaymentMethodRowData } from 'checkout/Components/CheckoutSteps/Payment/PurchaseBox/PaymentOptions/common'
import moment from 'moment'
import { calculateDaysBetweenDates } from './payInInstalmentsUtils'
import { sum } from 'lib/array/arrayUtils'
import { isBedbankItem, isCarHireItem, isCruiseItem, isExperienceItem, isFlightItem, isInstantBookingLEHotelItem, isTourItem, isTourV2ExperienceItem, isTransferItem, isVillaItem } from 'lib/checkout/checkoutUtils'

export const isKlarnaEnabled = (currency: string): boolean =>
  !!(config.KLARNA_ENABLED && getPaymentMethods(currency).includes(KLARNA_PAYMENT_TYPE))

export const isWithinKlarnaAvailabilityRange = (amount: number): boolean =>
  amount >= MIN_KLARNA_PAYMENT && amount <= MAX_KLARNA_PAYMENT

export const isKlarnaBpEnabled = (currency: string): boolean => {
  return config.KLARNA_BP_CURRENCIES ? isEnabledForFeature(config.KLARNA_BP_CURRENCIES, currency) && getPaymentMethods(currency).includes(KLARNA_BP_PAYMENT_TYPE) : false
}

export const isWithinKlarnaBpAvailabilityRange = (amount: number, currency: string): boolean => {
  switch (currency) {
    case 'NZD':
      return (amount >= MIN_KLARNA_BP_NZD_PAYMENT && amount <= MAX_KLARNA_BP_NZD_PAYMENT)
    case 'USD':
      return (amount >= MIN_KLARNA_BP_USD_PAYMENT && amount <= MAX_KLARNA_BP_USD_PAYMENT)
    case 'GBP':
      return (amount >= MIN_KLARNA_BP_GBP_PAYMENT && amount <= MAX_KLARNA_BP_GBP_PAYMENT)
    default:
      return false
  }
}

export const getKlarnaBpMaxLimit = (currency: string): number => {
  switch (currency) {
    case 'NZD':
      return MAX_KLARNA_BP_NZD_PAYMENT
    case 'USD':
      return MAX_KLARNA_BP_USD_PAYMENT
    case 'GBP':
      return MAX_KLARNA_BP_GBP_PAYMENT
    default:
      return 0
  }
}

export function calculateInstalments(amount: number, instalments: number): number {
  return Math.ceil(parseFloat(((amount / instalments) * 100).toFixed(3))) / 100
}

export function isBraintreeEnabled(currency: string) {
  return getPaymentMethods(currency).includes(BRAINTREE_PAYMENT_TYPE)
}

export function isPayPalEnabled(currency: string) {
  return config.PAYPAL_ENABLED && getPaymentMethods(currency).includes(PAYPAL_PAYMENT_TYPE)
}

export function getBNPLMethodAvailability(currencyCode: string, ...prices: Array<number>) {
  return {
    isKlarnaAvailable: isKlarnaEnabled(currencyCode) && prices.some(price => isWithinKlarnaAvailabilityRange(price)),
    isKlarnaBpAvailable: isKlarnaBpEnabled(currencyCode) && prices.some(price => isWithinKlarnaBpAvailabilityRange(price, currencyCode)),
    isAfterPayBpAvailable: isAfterPayBpEnabled(currencyCode) && prices.some(price => isWithinAfterPayBpAvailabilityRange(price, currencyCode)),
    isHoolahBpAvailable: isHoolahBpEnabled(currencyCode) && prices.some(price => isWithinHoolahBpAvailabilityRange(price, currencyCode)),
    isAtomeBpAvailable: isAtomeBpEnabled(currencyCode) && prices.some(price => isWithinAtomeBpAvailabilityRange(price, currencyCode)),
    isWaaveBpAvailable: isWaaveBpEnabled(currencyCode) && prices.some(price => isWithinWaaveBpAvailabilityRange(price, currencyCode)),
  }
}

export function isWithInStartDateRange(startDate: string, eligibilityRangeInDays: number) {
  if (!startDate) return true

  const currentDate = Date.now()
  const eligibilityRange = eligibilityRangeInDays * 24 * 60 * 60 * 1000

  const startDateInIsoFormat = new Date(startDate).toISOString()
  const startDateInMiliseonds = Date.parse(startDateInIsoFormat)

  return currentDate + eligibilityRange < startDateInMiliseonds
}

export function paymentMethodsToString(paymentMethods: Array<string> = []): string {
  const s = new Set<string>()
  paymentMethods.forEach(method => s.add(paymentMethodLabels[method] || method))
  const arr = Array.from(s)
  const formatter = new Intl.ListFormat('en', { type: 'disjunction' })
  return `This promo code applies to ${formatter.format(arr)}`
}

const merchantFeePaymentTypes: Array<App.MerchantFeePaymentType> = ['visa', 'mastercard', 'amex']
export function isCardAcceptedMerchantFeePaymentType(brand: string): brand is App.MerchantFeePaymentType {
  return merchantFeePaymentTypes.includes(brand as App.MerchantFeePaymentType)
}

export function getMerchantFeeByOrderItem(merchantFeeDetails: Array<App.MerchantFeeDetails>, itemId: string): App.MerchantFeeDetails | undefined {
  return merchantFeeDetails?.find(item => item.orderItemId === itemId)
}

function merchantFeeCaption(pspName: string, isGooglePayAvailable: boolean, isApplePayAvailable: boolean, isDebitCardMerchantFeesEnabled: boolean, isExpressPaymentAvailable: boolean = false) {
  let walletAvailability = ''
  // Don't show wallet availability in fee text if express payment is available
  if (!isExpressPaymentAvailable) {
    if (isGooglePayAvailable) {
      walletAvailability += ' and Google Pay'
    } else if (isApplePayAvailable) {
      walletAvailability += ' and Apple Pay'
    }
  }

  switch (pspName) {
    case BRAINTREE_PAYMENT_TYPE:
    case PAYPAL_PAYMENT_TYPE:
    case PAYLATER_PAYMENT_TYPE:
    case APPLE_PAY_PAYMENT_TYPE:
    case GOOGLE_PAY_PAYMENT_TYPE:
    case STRIPE_PAYMENT_ELEMENT_KLARNA_TYPE:
      return `Payment fee of ${BASE_MERCHANT_FEE}%`
    case GO_CARDLESS_PAYMENT_TYPE:
      return 'No Payment fee; transfer directly from your bank account'
    case STRIPE_CUSTOM_PAYTO_TYPE:
      return 'No Payment fee; Pay quickly and directly from your bank account with PayTo'
    case STRIPE_PAYMENT_ELEMENT_CARD_TYPE:
      if (isDebitCardMerchantFeesEnabled) {
        return `Debit card fees from ${DEBIT_CARD_MERCHANT_FEE}%, Credit card${walletAvailability} fees from ${BASE_MERCHANT_FEE}%`
      }
      return `No Debit card fee, Credit card${walletAvailability} fees from ${BASE_MERCHANT_FEE}%`
    case SECURE_PAYMENT_TYPE:
      return 'Generate a payment link to receive payment securely over the phone'
    case EXPRESS_PAYMENT_TYPE:
      return `Payment fee of ${BASE_MERCHANT_FEE}%`
    default:
      return null
  }
}

export function editPaymentMethodCaptions(
  paymentMethods: Array<PaymentMethodRowData>,
  isMerchantFeeAvailable: boolean,
  isStandaloneLuxPlusSubscription: boolean,
  isGooglePayAvailable: boolean,
  isApplePayAvailable: boolean,
  isExpressPaymentAvailable: boolean = false,
  isDebitCardMerchantFeesEnabled: boolean = false,
): Array<PaymentMethodRowData> {
  // No Merchant fees on LuxPLus
  if (!isMerchantFeeAvailable || isStandaloneLuxPlusSubscription) return paymentMethods

  const paymentMethodsWithUpdatedLabels = paymentMethods.map((paymentMethod) => {
    return { ...paymentMethod, subLabel: (merchantFeeCaption(paymentMethod.name, isGooglePayAvailable, isApplePayAvailable, isDebitCardMerchantFeesEnabled, isExpressPaymentAvailable) || paymentMethod.subLabel) }
  })

  return paymentMethodsWithUpdatedLabels
}

export function isStripePaymentCardV2(card: App.StripePaymentCard | App.StripePaymentCardV2): card is App.StripePaymentCardV2 {
  return 'funding' in card
}

export function shouldBlockShortLeadTimeWithCredit(payments: Array<App.PaymentInfo>, projectedLeadTimeDays: number): boolean {
  const creditAmount = sum(payments?.filter(payment => payment.type === 'credits') ?? [], (payment) => payment.amount)
  const orderAmount = sum(payments ?? [], (payment) => payment.amount)

  return config.FRAUD_SHORT_LEADTIME_PLUS_CREDITS_BLOCK_ENABLED &&
    !!orderAmount && // when order modification is not free for post purchase
    projectedLeadTimeDays <= SHORT_LEAD_TIME_WITH_CREDIT_MODAL_LEAD_TIME_DAYS &&
    (creditAmount / orderAmount) > SHORT_LEAD_TIME_WITH_CREDIT_MODAL_PERCENTAGE_THRESHOLD
}

export function getProjectedLeadTimeDaysByCartItems(cartItems: Array<App.Checkout.AnyItem>): number {
  const DEFAULT_LEAD_TIME = 999
  const leadTimeList = cartItems.map(cartItem => {
    if (
      isInstantBookingLEHotelItem(cartItem) || // Covers LPC and Flash
      isBedbankItem(cartItem) || // Covers LPP
      isVillaItem(cartItem)
    ) {
      return cartItem.checkIn
    }

    if (
      isCruiseItem(cartItem) ||
      isTourItem(cartItem)
    ) {
      return cartItem.startDate
    }

    if (
      isExperienceItem(cartItem) ||
      isTourV2ExperienceItem(cartItem)
    ) {
      return cartItem.date
    }

    if (
      isFlightItem(cartItem)
    ) {
      return cartItem.flights?.[0]?.departingDate ?? DEFAULT_LEAD_TIME
    }

    if (
      isCarHireItem(cartItem)
    ) {
      return cartItem.pickUpDate
    }

    if (
      isTransferItem(cartItem)
    ) {
      return cartItem.transfer.date
    }

    // Item does not appear to have a date so should assign date with default lead time.
    const today = moment.utc().format('YYYY-MM-DD')
    return moment(today).add(DEFAULT_LEAD_TIME, 'days').endOf('day').format('YYYY-MM-DD')
  }).filter(date => typeof date === 'string' && date).sort() as Array<string>

  if (!leadTimeList.length) {
    return DEFAULT_LEAD_TIME
  }

  return calculateDaysBetweenDates(leadTimeList[0])
}

export const paymentElementMethodMap: Utils.PartialRecord<PaymentMethod, string> = {
  [STRIPE_PAYMENT_ELEMENT_CARD_TYPE]: 'card',
  [STRIPE_PAYMENT_ELEMENT_AFTERPAY_TYPE]: 'afterpay_clearpay',
  [STRIPE_PAYMENT_ELEMENT_GIROPAY_TYPE]: 'giropay',
  [STRIPE_PAYMENT_ELEMENT_IDEAL_TYPE]: 'ideal',
  [STRIPE_PAYMENT_ELEMENT_KLARNA_TYPE]: 'klarna',
  [STRIPE_PAYMENT_ELEMENT_PAYNOW_TYPE]: 'paynow',
  [STRIPE_PAYMENT_ELEMENT_GRABPAY_TYPE]: 'grabpay',
  [STRIPE_PAYMENT_ELEMENT_WECHATPAY_TYPE]: 'wechat_pay',
  [STRIPE_CUSTOM_PAYTO_TYPE]: 'payto',
  [PAYPAL_PAYMENT_TYPE]: 'paypal',
  [PAYLATER_PAYMENT_TYPE]: 'paylater',
  [APPLE_PAY_PAYMENT_TYPE]: 'applepay',
  [GOOGLE_PAY_PAYMENT_TYPE]: 'googlepay',
  [VELOCITY_PAYMENT_TYPE]: 'velocity',
  [AZUPAY_PAY_ID_PAYMENT_TYPE]: 'azupay_pay_id',
  [FLYING_BLUE_PAYMENT_TYPE]: 'flying_blue',
  [GO_CARDLESS_PAYMENT_TYPE]: 'payto',
  [BRAINTREE_PAYMENT_TYPE]: 'card',
  [STRIPE_PAYMENT_TYPE]: 'card',
  [RAZORPAY_PAYMENT_TYPE]: 'card',
  [CREDIT_PAYMENT_TYPE]: 'credits',
  [SECURE_PAYMENT_TYPE]: 'card',
  [EXPRESS_PAYMENT_TYPE]: 'express',
  [STRIPE_TERMINAL_PAYMENT_TYPE]: 'card_present',
}

export const getMappedPaymentMethodForLogging = (
  paymentMethod: PaymentMethod | string | null | undefined,
  isApplePayAvailable?: boolean,
  isGooglePayAvailable?: boolean,
): string => {
  if (!paymentMethod) {
    return 'unknown'
  }

  // Handle express payment dynamic mapping
  if (paymentMethod === EXPRESS_PAYMENT_TYPE) {
    if (isApplePayAvailable && !isGooglePayAvailable) {
      return 'applepay'
    } else if (isGooglePayAvailable && !isApplePayAvailable) {
      return 'googlepay'
    } else if (isApplePayAvailable && isGooglePayAvailable) {
      // Both available, Apple Pay takes precedence (matches getExpressCheckoutPaymentType logic)
      return 'applepay'
    }
    // Fallback to express if neither available, should never get here
    return 'express'
  }

  return paymentElementMethodMap[paymentMethod as PaymentMethod] ?? 'unknown'
}
